import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Select, Spin, Empty, Tag, Tooltip, Popconfirm, Upload, message } from 'antd';
import { filmApi } from '../../services/api/film';
import { apiErrorText } from './apiErrorText';
import type { FilmImageVariant, ImageOwnerType, ImageModelInfo, SheetInputKind } from '../../types/film';
import { orderVariants, removeBlockedReason, modelOptionLabel } from './imageStudioUtils';
import { needsPromptRefetch, noFaceRefFlag, initialFaceRef, effectiveSheetKind, sheetModelOptions } from './faceSourceUtils';
import { useBankErrorReporter } from './WorkflowRunContext'; // P9D #6 (I10)
import PromptChipEditor from './PromptChipEditor'; // 261010 P9D #29 — ô Custom Prompt = chip
import { composerChoicesOf, composerPayloadOf } from './promptSurfaceUtils';
import type { RefKind } from './promptChipUtils';
import { isCustomVariant, markCustomVariant } from './customVariantMarks';
import './bank.css';

/**
 * Ô soạn gen ảnh dùng chung kiểu TopView (đợt 260927, mục A) — hàng ảnh tham chiếu (upload từ máy / chọn
 * trong Bank) + textarea điền sẵn prompt hệ thống + ô chọn model (chỉ tên, KHÔNG giá) + nút Gen.
 *
 * 🔴 TD (nhanh, đủ dùng — xem báo cáo Dev): mở dưới dạng Modal, KHÔNG "ngay trong thẻ" như TopView/spec mô
 * tả — dựng lại toàn bộ layout thẻ để nhúng ô soạn tốn thời gian hơn ngân sách đợt này cho phép; hành vi
 * (ref + prompt + model + gen) đủ để User bấm thử theo mục Kiểm của spec.
 *
 * Hai backend gen ảnh dùng CHUNG UI này (mục "một khung gen chung cho cả 3 tab"):
 *   · `studio` — Xưởng ảnh (`adminFilmImageStudioService`): Bối cảnh / Đạo cụ / ma-nơ-canh (LOOK cũ). Có
 *     dải "các bản đã gen" (film_image_variants) để chọn lại — y như trước.
 *   · `lookSheet` — ảnh NGƯỜI MẶC (`generateLookRef`, sheet nhân vật). KHÔNG có dải bản cũ (generateLookRef
 *     ghi đè tại chỗ, giống hành vi đã có từ trước đợt này) — TD ghi ở báo cáo, xem mục B.5 spec.
 */
export interface RefImage { key: string; url: string; label?: string; locked?: boolean; /** 261002 — ảnh tham chiếu MẶT/bộ chính của lượt gen look: gỡ được; gỡ ⇒ `noFaceRef` (dựng từ chữ). */ isFaceRef?: boolean }

export type ComposerBackend =
  | { kind: 'studio'; ownerType: ImageOwnerType; ownerId: string }
  | { kind: 'lookSheet'; lookId: string; sheetModels: ImageModelInfo[]; /** 261002 (F-03): cho gợi ý model tính lại theo loại ảnh vào sau khi gỡ ref. */ allModels?: ImageModelInfo[]; sheetInput?: SheetInputKind | null };

interface Props {
  open: boolean;
  onClose: () => void;
  /** Ảnh đang dùng đổi (chọn bản mới ở studio, hoặc gen xong ở lookSheet) — màn cha tải lại. */
  onChanged?: () => void;
  backend: ComposerBackend;
  planId?: string | null;
  title?: string;
  /** Ảnh tham chiếu mặc định của look phụ (sheet bộ chính). 261002: KHÔNG còn khoá — gỡ được (⇒ dựng từ chữ). */
  lockedRef?: RefImage;
  /** Ảnh có sẵn trong Bank của truyện để chọn thay upload ("＋ Ref" → chọn trong Bank). */
  bankImages?: RefImage[];
}

const readAsDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result));
  r.onerror = () => reject(new Error('read failed'));
  r.readAsDataURL(file);
});

const ImageComposer: React.FC<Props> = ({ open, onClose, onChanged, backend, planId, title, lockedRef, bankImages }) => {
  const isStudio = backend.kind === 'studio';
  const reportError = useBankErrorReporter();
  const [prompt, setPrompt] = useState('');
  const [origPrompt, setOrigPrompt] = useState('');
  const [label, setLabel] = useState('');
  const [refs, setRefs] = useState<RefImage[]>([]);
  const [variants, setVariants] = useState<FilmImageVariant[]>([]);
  const [models, setModels] = useState<ImageModelInfo[]>([]);
  const [model, setModel] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [gen, setGen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // 261030 B1: bản gen bằng prompt tay (Custom Prompt) ⇒ khi bấm «Dùng», báo BE ghi nguồn «ảnh của người» (máy không gen đè).
  // B1-fix1: dấu này sống qua đóng/mở composer (`customVariantMarks`), không còn là useRef của composer.
  const [hadFaceRef, setHadFaceRef] = useState(false);
  // P9D #29: chip editor nạp chữ MỘT lần ⇒ đổi `promptRev` mỗi khi prompt bị thay từ code (mở ô / nạp lại từ chữ) để nó dựng lại.
  const [promptRev, setPromptRev] = useState(0);
  const chipFallback: RefKind = backend.kind === 'studio' ? (backend.ownerType === 'LOCATION' ? 'loc' : backend.ownerType === 'PROP' ? 'prop' : 'look') : 'look';
  const chipChoices = useMemo(() => composerChoicesOf(bankImages, chipFallback), [bankImages, chipFallback]);

  // 261002 (F-03): gợi ý model tính lại theo loại ảnh vào — gỡ ref mặt/bộ chính ⇒ 'text'.
  const faceRefRemoved = hadFaceRef && !refs.some((r) => r.isFaceRef);
  const modelsShown = backend.kind === 'lookSheet' && backend.allModels
    ? sheetModelOptions(backend.allModels, effectiveSheetKind(backend.sheetInput, faceRefRemoved))
    : models;

  const reloadVariants = useCallback(async () => {
    if (backend.kind !== 'studio') return;
    setVariants(await filmApi.imageStudioVariants(backend.ownerType, backend.ownerId));
  }, [backend]);

  /**
   * 28/09 (chỗ vấp #5b, bấm thật) — định danh ổn định của "phiên soạn" hiện tại. Cha (`FilmReferenceTab`…)
   * dựng object `backend`/`lockedRef` MỚI ở mỗi lần re-render (ví dụ sau `reloadWardrobe()` không liên quan
   * tới modal này) dù nội dung logic y hệt. `load()` từng phụ vào chính các object đó ⇒ chạy lại mỗi khi cha
   * re-render, GHI ĐÈ mất prompt/model/ref người dùng đang gõ. `composerKey` chỉ đổi khi mở cho owner/lookId
   * KHÁC — đó mới là lúc cần nạp lại từ đầu.
   */
  const composerKey = backend.kind === 'studio' ? `studio:${backend.ownerType}:${backend.ownerId}` : `lookSheet:${backend.lookId}`;

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      if (backend.kind === 'studio') {
        const [p, vs, ms] = await Promise.all([
          filmApi.imageStudioPrompt(backend.ownerType, backend.ownerId, planId),
          filmApi.imageStudioVariants(backend.ownerType, backend.ownerId),
          filmApi.imageStudioModels(),
        ]);
        setPrompt(p.prompt); setOrigPrompt(p.prompt); setPromptRev((n) => n + 1); setLabel(p.label); setVariants(vs); setModels(ms);
        setModel((cur) => cur);
      } else {
        const p = await filmApi.sheetPrompt(backend.lookId, planId);
        setPrompt(p.prompt); setOrigPrompt(p.prompt); setPromptRev((n) => n + 1); setLabel(p.label); setVariants([]); setModels(backend.sheetModels);
        setModel(undefined);
        // 261002: ảnh tham chiếu mặt/bộ chính = ảnh server SẼ dùng (`faceUrl`; bộ phụ = ảnh bộ chính) — GỠ ĐƯỢC, không khoá.
        // 261002 (review F-01): bộ chính sắp dựng mặt Flare MỚI (`freshFace`) cũng có ô "Mặt mới (Flare)" gỡ được.
        const face = initialFaceRef(p, lockedRef?.label);
        setRefs(face ? [face] : []); setHadFaceRef(!!face);
        return;
      }
      setRefs([]); setHadFaceRef(false);
    } catch (e: any) { message.error(apiErrorText(e, 'Mở ô soạn')); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cố ý KHÔNG đưa `backend`/`lockedRef` (object mới
    // mỗi render) vào đây; `composerKey` đại diện đúng thứ cần theo dõi (xem chú thích trên).
  }, [open, composerKey, planId]);

  useEffect(() => { load(); }, [load]);

  const addFile = async (file: File) => {
    try {
      const dataUrl = await readAsDataUrl(file);
      setRefs((list) => [...list, { key: `up-${Date.now()}-${file.name}`, url: dataUrl, label: file.name }]);
    } catch { message.error('Đọc ảnh thất bại'); }
  };
  const removeRef = async (key: string) => {
    const gone = refs.find((r) => r.key === key);
    setRefs((list) => list.filter((r) => r.key !== key));
    // 261002: gỡ ảnh mặt/bộ chính ⇒ prompt điền sẵn phải là bản TỪ CHỮ; người dùng đã sửa prompt thì giữ chữ của họ.
    if (gone?.isFaceRef && backend.kind === 'lookSheet' && needsPromptRefetch({ prompt, origPrompt })) {
      try { const p = await filmApi.sheetPrompt(backend.lookId, planId, true); setPrompt(p.prompt); setOrigPrompt(p.prompt); setPromptRev((n) => n + 1); }
      catch (e: any) { message.error(apiErrorText(e, 'Nạp prompt từ chữ')); }
    }
  };
  const pickBank = (img: RefImage) => {
    setRefs((list) => (list.some((r) => r.url === img.url) ? list : [...list, img]));
    setPickerOpen(false);
  };

  const doGenerate = async () => {
    setGen(true);
    // ảnh mặt/bộ chính do SERVER tự đính (không gửi lại); chỉ ảnh người dùng thêm đi theo `refImages`.
    // P9D #29: chip trong prompt → nhãn chữ + ảnh của chip vào refImages (BE nhận cả hai — 0 thay đổi BE).
    const { promptOverride, refImages: extraRefs } = composerPayloadOf({ text: prompt, origText: origPrompt, refs, choices: chipChoices });
    const noFaceRef = backend.kind === 'lookSheet' && noFaceRefFlag(refs, hadFaceRef);
    try {
      if (backend.kind === 'studio') {
        const r = await filmApi.imageStudioGenerate(backend.ownerType, backend.ownerId, {
          prompt: promptOverride, model, planId, refImages: extraRefs,
        });
        if (promptOverride && r.variant?.id) markCustomVariant(r.variant.id);
        message.success('Đã gen một bản mới — bấm "Dùng" ở bản bạn ưng, ảnh đang dùng chưa đổi.');
        (r.warnings || []).forEach((w) => message.warning(w, 8));
        await reloadVariants();
      } else {
        const saved = await filmApi.generateLookRef(backend.lookId, { planId, model, promptOverride, refImages: extraRefs, noFaceRef });
        message.success('Đã gen ảnh người mặc mới.');
        (saved.warnings || []).forEach((w) => message.warning(w, 8));
        onChanged?.();
        onClose();
      }
    } catch (e: any) {
      // 260927 mục N7 — câu lỗi "bị kiểm duyệt" + gợi ý model khác/mô tả kín hơn (server dựng sẵn, xem
      // `adminFilmLookService.genSheetImage`) có thể dài — 8s cho kịp đọc, cùng độ dài với các `message.warning` khác.
      reportError(e, 'Gen ảnh', (err) => message.error({ content: apiErrorText(err, 'Gen ảnh'), duration: 8 }));
    }
    finally { setGen(false); }
  };

  const doChoose = async (v: FilmImageVariant) => {
    try {
      await filmApi.imageStudioChoose(v.id, planId, isCustomVariant(v.id) ? 'CUSTOM_PROMPT' : undefined);
      message.success('Đã đặt làm ảnh đang dùng');
      await reloadVariants();
      onChanged?.();
    } catch (e: any) { message.error(apiErrorText(e, 'Dùng bản này')); }
  };
  const doRemove = async (v: FilmImageVariant) => {
    try { await filmApi.imageStudioRemove(v.id); await reloadVariants(); }
    catch (e: any) { message.error(apiErrorText(e, 'Xoá bản')); }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={980} destroyOnClose title={`Custom Prompt — ${title || label || ''}`}>
      {loading ? (
        <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
      ) : (
        <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 460px', minWidth: 340 }}>
            <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600 }}>Ảnh tham chiếu</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'flex-start' }}>
              {refs.map((r) => (
                <div key={r.key} style={{ position: 'relative', width: 64, height: 64 }}>
                  <img src={r.url} alt={r.label || ''} title={r.label}
                    style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: r.locked ? '2px solid var(--sb-accent,#1677ff)' : '1px solid #ddd' }} />
                  {r.locked
                    ? <Tooltip title={r.label || 'Ảnh khoá'}><span style={{ position: 'absolute', top: -6, right: -6, fontSize: 13 }}>🔒</span></Tooltip>
                    : (
                      <button type="button" aria-label="Gỡ ảnh tham chiếu" onClick={() => removeRef(r.key)}
                        style={{ position: 'absolute', top: -6, right: -6, border: 0, borderRadius: '50%', width: 18, height: 18, background: '#fff', boxShadow: '0 0 2px rgba(0,0,0,.4)', cursor: 'pointer', lineHeight: 1 }}>✕</button>
                    )}
                </div>
              ))}
              <Upload accept="image/*" showUploadList={false} customRequest={({ file }) => addFile(file as File)}>
                <Button style={{ width: 64, height: 64 }}>＋ Ref</Button>
              </Upload>
              {!!bankImages?.length && (
                <Button style={{ height: 64 }} onClick={() => setPickerOpen(true)}>Chọn ảnh trong Bank…</Button>
              )}
            </div>
            <PromptChipEditor key={promptRev} inline value={prompt} extraChoices={chipChoices} onChange={setPrompt} minHeight={220} />
            <div className="faint" style={{ fontSize: 11, marginTop: 4 }}>Gõ <b>@</b> để chèn chip ảnh trong Bank vào prompt — chip thành tên trong câu và ảnh của nó được đính kèm khi Gen.</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <Select value={model} onChange={setModel} allowClear placeholder="Model mặc định" style={{ minWidth: 260 }}
                options={modelsShown.map((m) => ({ value: m.id, label: modelOptionLabel(m) }))} />
              <Button type="primary" loading={gen} onClick={doGenerate}>↑ Gen</Button>
            </div>
          </div>

          {isStudio && (
            <div style={{ flex: '1 1 320px', minWidth: 280 }}>
              <b style={{ fontSize: 13 }}>Các bản đã gen ({variants.length})</b>
              {!variants.length ? (
                <Empty description="Chưa có bản nào — bấm Gen để tạo bản đầu" style={{ margin: '24px 0' }} />
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 8, marginTop: 8, maxHeight: 440, overflowY: 'auto' }}>
                  {orderVariants(variants).map((v) => {
                    const blocked = removeBlockedReason(v);
                    return (
                      <div key={v.id} style={{ border: v.isChosen ? '2px solid var(--sb-accent,#1677ff)' : '1px solid var(--sb-line,#eee)', borderRadius: 6, padding: 4 }}>
                        <img src={v.url} alt="" style={{ width: '100%', borderRadius: 4, display: 'block' }} />
                        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                          {v.isChosen ? <Tag color="blue" style={{ margin: 0 }}>Đang dùng</Tag> : <Button size="small" onClick={() => doChoose(v)}>Dùng</Button>}
                          <Popconfirm title="Xoá bản này?" onConfirm={() => doRemove(v)} disabled={!!blocked}>
                            <Button size="small" danger disabled={!!blocked}>✕</Button>
                          </Popconfirm>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Modal open={pickerOpen} onCancel={() => setPickerOpen(false)} footer={null} title="Chọn ảnh trong Bank của truyện">
        {!bankImages?.length ? <Empty description="Chưa có ảnh nào trong Bank" /> : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {bankImages.map((img) => (
              <button key={img.key} type="button" onClick={() => pickBank(img)} style={{ border: 0, padding: 0, cursor: 'pointer', background: 'none' }}>
                <img src={img.url} alt={img.label || ''} title={img.label} style={{ width: 76, height: 76, objectFit: 'cover', borderRadius: 6 }} />
              </button>
            ))}
          </div>
        )}
      </Modal>
    </Modal>
  );
};

export default ImageComposer;
